import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  Agent,
  AgentSubmission,
  AgentTool,
  Comment,
  ItemType,
  Prompt,
  PromptCategory,
  PromptStatus,
  PromptSubmission,
  SortOption,
  Vote,
} from '@internal/backstage-plugin-ai-library-common';

export const aiLibraryApiRef = createApiRef<AiLibraryApi>({
  id: 'plugin.ai-library.api',
});

export type PromptQuery = {
  status?: PromptStatus;
  category?: PromptCategory;
  search?: string;
  sort?: SortOption;
};

export type AgentQuery = PromptQuery & { tool?: AgentTool };

export interface AiLibraryApi {
  getPrompts(options?: PromptQuery): Promise<Prompt[]>;
  getPrompt(id: number): Promise<Prompt>;
  submitPrompt(prompt: PromptSubmission): Promise<Prompt>;
  updatePrompt(id: number, updates: Partial<PromptSubmission>): Promise<Prompt>;
  deletePrompt(id: number): Promise<void>;

  getAgents(options?: AgentQuery): Promise<Agent[]>;
  getAgent(id: number): Promise<Agent>;
  submitAgent(agent: AgentSubmission): Promise<Agent>;
  updateAgent(id: number, updates: Partial<AgentSubmission>): Promise<Agent>;
  deleteAgent(id: number): Promise<void>;

  getUserVotes(): Promise<Vote[]>;
  castVote(
    itemId: number,
    itemType: ItemType,
    voteType: 'up' | 'down',
  ): Promise<{ action: string }>;

  getComments(itemId: number, itemType: ItemType): Promise<Comment[]>;
  addComment(
    itemId: number,
    itemType: ItemType,
    itemTitle: string,
    text: string,
  ): Promise<Comment>;
  deleteComment(id: number): Promise<void>;

  isAdmin(): Promise<boolean>;
  getPendingItems(): Promise<{ prompts: Prompt[]; agents: Agent[] }>;
  approveItem(type: ItemType, id: number): Promise<void>;
  rejectItem(type: ItemType, id: number): Promise<void>;

  incrementViewCount(
    itemType: ItemType,
    id: number,
  ): Promise<{ view_count: number }>;
}

export class AiLibraryClient implements AiLibraryApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;

  constructor(options: { fetchApi: FetchApi; discoveryApi: DiscoveryApi }) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  /** `fetchApi` attaches the caller's Backstage token itself. */
  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-library');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Request failed with status ${response.status}`,
      );
    }

    return response.status === 204 ? (undefined as T) : response.json();
  }

  private query(options: Record<string, string | undefined> = {}): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(options)) {
      if (value) {
        params.append(key, value);
      }
    }
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  // ============ PROMPTS ============

  async getPrompts(options: PromptQuery = {}): Promise<Prompt[]> {
    return this.fetch(`/prompts${this.query(options)}`);
  }

  async getPrompt(id: number): Promise<Prompt> {
    return this.fetch(`/prompts/${id}`);
  }

  async submitPrompt(prompt: PromptSubmission): Promise<Prompt> {
    return this.fetch('/prompts', {
      method: 'POST',
      body: JSON.stringify(prompt),
    });
  }

  async updatePrompt(
    id: number,
    updates: Partial<PromptSubmission>,
  ): Promise<Prompt> {
    return this.fetch(`/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePrompt(id: number): Promise<void> {
    await this.fetch(`/prompts/${id}`, { method: 'DELETE' });
  }

  // ============ AGENTS ============

  async getAgents(options: AgentQuery = {}): Promise<Agent[]> {
    return this.fetch(`/agents${this.query(options)}`);
  }

  async getAgent(id: number): Promise<Agent> {
    return this.fetch(`/agents/${id}`);
  }

  async submitAgent(agent: AgentSubmission): Promise<Agent> {
    return this.fetch('/agents', {
      method: 'POST',
      body: JSON.stringify(agent),
    });
  }

  async updateAgent(
    id: number,
    updates: Partial<AgentSubmission>,
  ): Promise<Agent> {
    return this.fetch(`/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteAgent(id: number): Promise<void> {
    await this.fetch(`/agents/${id}`, { method: 'DELETE' });
  }

  // ============ VOTES ============

  async getUserVotes(): Promise<Vote[]> {
    return this.fetch('/votes');
  }

  async castVote(
    itemId: number,
    itemType: ItemType,
    voteType: 'up' | 'down',
  ): Promise<{ action: string }> {
    return this.fetch('/votes', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        vote_type: voteType,
      }),
    });
  }

  // ============ COMMENTS ============

  async getComments(itemId: number, itemType: ItemType): Promise<Comment[]> {
    return this.fetch(
      `/comments${this.query({ itemId: String(itemId), itemType })}`,
    );
  }

  async addComment(
    itemId: number,
    itemType: ItemType,
    itemTitle: string,
    text: string,
  ): Promise<Comment> {
    return this.fetch('/comments', {
      method: 'POST',
      body: JSON.stringify({
        item_id: itemId,
        item_type: itemType,
        item_title: itemTitle,
        comment_text: text,
      }),
    });
  }

  async deleteComment(id: number): Promise<void> {
    await this.fetch(`/comments/${id}`, { method: 'DELETE' });
  }

  // ============ ADMIN ============

  async isAdmin(): Promise<boolean> {
    try {
      const result = await this.fetch<{ isAdmin: boolean }>('/admin/is-admin');
      return result.isAdmin;
    } catch {
      return false;
    }
  }

  async getPendingItems(): Promise<{ prompts: Prompt[]; agents: Agent[] }> {
    return this.fetch('/admin/pending');
  }

  async approveItem(type: ItemType, id: number): Promise<void> {
    await this.fetch(`/admin/approve/${type}/${id}`, { method: 'POST' });
  }

  async rejectItem(type: ItemType, id: number): Promise<void> {
    await this.fetch(`/admin/reject/${type}/${id}`, { method: 'POST' });
  }

  // ============ VIEWS ============

  async incrementViewCount(
    itemType: ItemType,
    id: number,
  ): Promise<{ view_count: number }> {
    return this.fetch(`/views/${itemType}/${id}`, { method: 'POST' });
  }
}
